import { useCallback, useRef } from 'react';
import { apiService, type Conversation as ApiConversation } from '../../../services/api';
import type { ChatMessage } from '../../ChatPanel';
import logger from '../../../utils/simpleLogger';
import type { MentionFilters } from '../../../types/mention';
import { useMessagePreparer } from './useMessagePreparer';
import { useStreamingHandlers } from './useStreamingHandlers';
import { eventBus, EventNames } from '../../../utils/eventBus';

/** Generate a clean title from user message text (max 40 chars, word boundary) */
function generateConversationTitle(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= 40) return clean;
  const truncated = clean.substring(0, 40);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 20 ? truncated.substring(0, lastSpace) : truncated) + '…';
}

/**
 * Hook para gestionar el envío de mensajes con streaming.
 *
 * Orchestrates useMessagePreparer (validation, optimistic UI)
 * and useStreamingHandlers (RAF throttling, finalization, errors).
 */

export interface UseSendMessageOptions {
  conversation: ApiConversation | null;
  selectedDocuments: string[];
  onMessagesUpdate: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  onConversationCreate?: (name: string) => Promise<ApiConversation | null>;
  streamAbortControllerRef: React.MutableRefObject<AbortController | null>;
  streamingMessageIdRef: React.MutableRefObject<string | null>;
  cleanupStreamingRefs: () => void;
  startTyping: () => void;
  stopTyping: () => void;
  startLoading: () => void;
  stopLoading: () => void;
  setError: (error: string | null) => void;
  handleStreamCancellation: () => void;
  startAbsoluteTimeout: () => void;
  onStreamingEnd?: () => void;
  onSuggestion?: (data: {
    message?: string;
    suggested_documents?: Array<{ id: string; name: string }>;
    reason?: string;
  }) => void;
  onStreamingTimeout?: (isTimedOut: boolean) => void;
}

interface SendMessageState {
  inputMessage: string;
  contextFilters: MentionFilters;
}

export function useSendMessage(options: UseSendMessageOptions) {
  const {
    conversation,
    selectedDocuments,
    onMessagesUpdate,
    onConversationCreate,
    streamAbortControllerRef,
    streamingMessageIdRef,
    cleanupStreamingRefs,
    startTyping,
    startLoading,
    stopLoading,
    stopTyping,
    setError,
    handleStreamCancellation,
    startAbsoluteTimeout,
    onStreamingEnd,
    onSuggestion,
    onStreamingTimeout,
  } = options;

  const { validateAndPrepare, ensureConversation, addOptimisticMessage, createStreamingMessage } =
    useMessagePreparer({
      conversation,
      selectedDocuments,
      onMessagesUpdate,
      onConversationCreate,
    });

  const {
    batchAccumulator,
    scheduleUpdate,
    flushPendingUpdate,
    cancelPendingRAF,
    finalizeMessage,
    handleStreamError,
  } = useStreamingHandlers({
    onMessagesUpdate,
    stopTyping,
    stopLoading,
    setError,
    handleStreamCancellation,
    onStreamingEnd,
    onStreamingTimeout,
  });

  // Generation counter to detect stale callbacks from aborted streams
  const streamGenerationRef = useRef(0);

  // Track which conversations have already been auto-titled (prevent duplicates)
  const autoTitledRef = useRef(new Set<string>());

  /** Auto-rename "Nueva conversación" to a title based on first user message */
  const autoTitleIfNeeded = useCallback((conv: ApiConversation, messageText: string) => {
    if (conv.name !== 'Nueva conversación') return;
    if (autoTitledRef.current.has(conv.id)) return;
    autoTitledRef.current.add(conv.id);

    const newTitle = generateConversationTitle(messageText);
    logger.info('✏️ [AutoTitle] Renaming conversation', { id: conv.id, newTitle });

    // Update backend
    apiService.updateConversation(conv.id, { name: newTitle }).catch((err) => {
      logger.warn('⚠️ [AutoTitle] Failed to rename', err);
    });

    // Notify UI (MainLayout) to update local state
    eventBus.emit(EventNames.CONVERSATION_AUTO_TITLED, {
      conversationId: conv.id,
      name: newTitle,
    });
  }, []);

  /**
   * Execute streaming API call
   */
  const executeStreaming = useCallback(
    async (
      activeConversation: ApiConversation,
      messageText: string,
      finalSelectedDocuments: string[],
      tempUserMessage: ChatMessage,
      setInputMessage: (value: string) => void,
      messageTextOverride?: string,
      studio_id?: string
    ) => {
      const streamingMessageId = `streaming-${Date.now()}`;
      streamingMessageIdRef.current = streamingMessageId;

      // Abort previous stream FIRST to prevent stale tokens from leaking in
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      streamAbortControllerRef.current = abortController;

      // Start absolute timeout safety net (auto-aborts after 5 min if stream hangs)
      startAbsoluteTimeout();

      // Cancel any pending RAF from previous stream to prevent stale updates
      cancelPendingRAF();

      // Increment generation counter — stale callbacks will detect mismatch
      const generation = ++streamGenerationRef.current;

      // Reset accumulator AFTER aborting old stream to prevent race condition
      batchAccumulator.reset();

      if (!messageTextOverride) {
        setInputMessage('');
      }

      let streamingMessageCreated = false;

      try {
        await apiService.sendMessageStream(
          {
            message: messageText,
            conversation_id: activeConversation.id,
            selected_documents: finalSelectedDocuments,
            ...(studio_id && { studio_id }),
          },
          {
            onStatus: (data) => {
              if (generation !== streamGenerationRef.current) return;
              logger.debug('📊 Status received:', data.status, data.code);
              const code = data.code;
              const status = data.status || '';

              // Route by structured code (preferred) with string-match fallback
              const isInfoCode = code === 'NO_DOCS_FOUND' || code === 'NO_DOCS_UPLOADED';
              const isActionCode = code === 'SELECT_DOCS';
              const isProgressCode =
                code === 'CLASSIFYING' ||
                code === 'EXECUTION_PLAN' ||
                code === 'SEARCHING' ||
                code === 'PROCESSING' ||
                code === 'MODE_INFO';

              // Fallback: string matching for backwards compatibility (when code is absent)
              const isInfoFallback =
                !code &&
                (status.includes('No encontré') ||
                  status.includes('seleccionar alguno') ||
                  status.includes('No hay documentos') ||
                  status.includes('No tienes documentos'));
              const isActionFallback =
                !code &&
                (status.includes('primero selecciona') || status.includes('Selecciona documentos'));

              if (isInfoCode || isInfoFallback) {
                // Insert friendly assistant message instead of red error
                const infoMessage: ChatMessage = {
                  id: `info-${Date.now()}`,
                  text: status,
                  isUser: false,
                  timestamp: new Date(),
                  markdown: status,
                };
                onMessagesUpdate((prev) => [...prev, infoMessage]);
                stopTyping();
                stopLoading();
              } else if (isActionCode || isActionFallback) {
                setError(status);
                stopTyping();
                stopLoading();
              }
              // Progress codes — emit status text for TypingIndicator
              if (isProgressCode) {
                const statusMap: Record<string, string> = {
                  CLASSIFYING: 'Analizando tu pregunta...',
                  EXECUTION_PLAN: status || 'Planificando respuesta...',
                  SEARCHING: 'Revisando tus documentos...',
                  PROCESSING: status || 'Preparando respuesta...',
                  MODE_INFO: status,
                };
                eventBus.emit(EventNames.PROCESSING_STATUS_CHANGED, {
                  text: statusMap[code!] || status,
                  code: code,
                });
              }
            },
            onBatchStart: (data) => {
              if (generation !== streamGenerationRef.current) return;
              logger.debug(`🔵 Batch ${data.batch_number}/${data.total_batches} started`);
              batchAccumulator.handleBatchStart(data.batch_number, data.total_batches || 0);

              if (data.batch_number === 1) {
                streamingMessageCreated = true;
                createStreamingMessage(streamingMessageId, tempUserMessage.id);
              }
            },
            onBatchToken: (data) => {
              if (generation !== streamGenerationRef.current) return;
              if (!data.batch_number) return;
              const text = batchAccumulator.accumulateText(
                data.batch_number,
                data.full_response_so_far || ''
              );
              scheduleUpdate(text, streamingMessageId);
            },
            onBatchComplete: (data) => {
              if (generation !== streamGenerationRef.current) return;
              logger.debug(`✅ Batch ${data.batch_number} completed`);
              cancelPendingRAF();
              flushPendingUpdate(streamingMessageId);
              batchAccumulator.completeBatch(
                data.batch_number,
                data.response || '',
                data.citations
              );
            },
            onBatchError: (data) => {
              if (generation !== streamGenerationRef.current) return;
              console.error(`❌ Batch ${data.batch_number} error:`, data.error);
            },
            onFinalResult: (data) => {
              if (generation !== streamGenerationRef.current) return;
              logger.debug(
                `✅ [FINAL_RESULT] Streaming completo: ${data.batches_completed} de ${data.total_batches} batches`
              );
              finalizeMessage(
                tempUserMessage,
                streamingMessageId,
                {
                  response: data.response || '',
                  citations: data.citations || {},
                  usageStats: data.total_usage_stats,
                  ragStats: data.total_rag_stats,
                  messageId: data.message_id,
                  userMessageId: data.user_message_id,
                  autoSelectionInfo: (data as any).auto_selection_info || undefined,
                },
                true
              );
              // QW8: Auto-title "Nueva conversación" after first response
              autoTitleIfNeeded(activeConversation, messageText);
            },
            onToken: (data) => {
              if (generation !== streamGenerationRef.current) return;
              if (!streamingMessageCreated) {
                streamingMessageCreated = true;
                createStreamingMessage(streamingMessageId, tempUserMessage.id);
              }
              scheduleUpdate(data.full_response_so_far || '', streamingMessageId);
            },
            onComplete: (data) => {
              if (generation !== streamGenerationRef.current) return;
              logger.debug(`✅ [COMPLETE] Simple response completed`);
              const dataAny = data as any;
              finalizeMessage(
                tempUserMessage,
                streamingMessageId,
                {
                  response: data.response || '',
                  citations: data.citations || {},
                  usageStats: data.usage_stats,
                  ragStats: data.rag_stats,
                  aiFlowTrace: dataAny.ai_flow_trace,
                  messageId: data.message_id,
                  userMessageId: data.user_message_id,
                  autoSelectionInfo: dataAny.auto_selection_info || undefined,
                },
                false
              );
              // QW8: Auto-title "Nueva conversación" after first response
              autoTitleIfNeeded(activeConversation, messageText);
            },
            onSuggestion: (data) => {
              logger.info('💡 Document suggestion received:', {
                message: data.message,
                documents: data.suggested_documents?.map((d) => d.name),
                reason: data.reason,
              });
              // B5: Map transparency fields (reason, strategy, score) from backend
              const docs = data.suggested_documents?.map((d: any) => ({
                id: d.id,
                name: d.name ?? d.id,
                reason: d.reason,
                strategy: d.strategy,
                score: d.relevance,
              }));
              if (docs?.length) onSuggestion?.({ ...data, suggested_documents: docs });
            },
            onError: (error) => {
              handleStreamError(error);
            },
            abortSignal: abortController.signal,
          }
        );
      } catch (error) {
        handleStreamError(error instanceof Error ? error : new Error(String(error)));
      } finally {
        // Only cleanup refs if this is still the current stream generation
        // (a newer stream may have already started and set its own refs)
        if (generation === streamGenerationRef.current) {
          cleanupStreamingRefs();
          // Safety net: ensure typing/loading states are always cleared
          // even if SSE stream ends silently without onComplete/onError
          stopTyping();
          stopLoading();
        }
      }
    },
    [
      batchAccumulator,
      streamAbortControllerRef,
      streamingMessageIdRef,
      cleanupStreamingRefs,
      createStreamingMessage,
      scheduleUpdate,
      flushPendingUpdate,
      cancelPendingRAF,
      finalizeMessage,
      handleStreamError,
      setError,
      stopTyping,
      stopLoading,
      onSuggestion,
    ]
  );

  /**
   * Main send message function
   */
  const sendMessage = useCallback(
    async (
      state: SendMessageState,
      setInputMessage: (value: string) => void,
      messageTextOverride?: string,
      options?: { studio_id?: string; fallback_document_ids?: string[] }
    ) => {
      const { textToSend, hasText, hasFilters, isValid } = validateAndPrepare(
        state,
        messageTextOverride
      );

      logger.info('🎯 sendMessage called', {
        hasText,
        hasFilters,
        conversationId: conversation?.id,
        selectedDocumentCount: selectedDocuments.length,
      });

      if (!isValid) {
        logger.warn('⚠️ sendMessage blocked - missing content');
        return;
      }

      const messageText = textToSend.trim();
      setError(null);

      // Add optimistic message BEFORE ensureConversation to prevent
      // EmptyStateView flash. React 18 microtask batching can flush
      // setCurrentConversation (from createConversation) before
      // addOptimisticMessage runs, causing a render with
      // currentConversation set but messages still empty.
      const tempUserMessage = addOptimisticMessage(messageText);
      startTyping();
      startLoading();

      let activeConversation: ApiConversation | null;
      try {
        activeConversation = await ensureConversation(textToSend, isValid);
      } catch (err) {
        logger.error('❌ Failed to ensure conversation', err);
        setError('No se pudo crear la conversación automáticamente');
        // Clean up optimistic message on failure
        onMessagesUpdate((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
        stopTyping();
        stopLoading();
        return;
      }

      if (!activeConversation) {
        logger.warn('⚠️ sendMessage blocked - no conversation');
        // Clean up optimistic message
        onMessagesUpdate((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
        stopTyping();
        stopLoading();
        return;
      }

      const finalSelectedDocuments =
        selectedDocuments.length > 0
          ? selectedDocuments
          : options?.fallback_document_ids?.length
            ? options.fallback_document_ids
            : [];

      logger.info('🤖 Streaming response initiated', {
        conversationId: activeConversation.id,
        selectedDocuments: finalSelectedDocuments,
      });

      await executeStreaming(
        activeConversation,
        messageText,
        finalSelectedDocuments,
        tempUserMessage,
        setInputMessage,
        messageTextOverride,
        options?.studio_id
      );
    },
    [
      conversation,
      selectedDocuments,
      validateAndPrepare,
      ensureConversation,
      addOptimisticMessage,
      executeStreaming,
      onMessagesUpdate,
      startTyping,
      startLoading,
      stopTyping,
      stopLoading,
      setError,
    ]
  );

  return {
    sendMessage,
    validateAndPrepare,
    ensureConversation,
    addOptimisticMessage,
    executeStreaming,
    handleStreamError,
  };
}
